/**
 * Exchanges status filter → PBHOrdersSQLOnly OData `$filter` (DE-9312).
 *
 * The toolbar still uses human-readable labels and splits inbound vs outbound
 * via `buildStatusFilterParams` → `inboundStatus` / `outboundStatus` on the
 * URL. This module is the bridge to the SQL-only entity, which has no
 * `InboundStatus` / `OutboundStatus` columns — only Raw* fields.
 *
 * Flow:
 *   User selects "Shipped" → URL `outboundStatus=Shipped` →
 *   `orderQueryODataSqlOnly` → `buildSqlOnlyStatusClause` →
 *   OData clause aligned with `mapRawOutboundDiscriminator` (outbound
 *   `ShipmentAction` **or** generic/null outbound + `RawStatus` shipment)
 *
 * Curated mappings live in `UI_STATUS_TO_ODATA`. Additional labels from the static
 * dropdown (`exchangesStatusFilters.ts`) resolve via `resolveDynamicSqlOnlyStatusFilter`.
 */

import { getStatusType } from "../../Orders.PBH/utils/statusUtils";
import {
  buildDisplayToRawDiscriminatorMap,
  NEUTRAL_COVERAGE_LABEL,
  NEUTRAL_STOCK_LABEL,
  OFF_CORE_DETAIL_LABEL,
} from "./sqlOnlyOrderMapper";

export interface SqlOnlyStatusODataFilter {
  /** OData $filter fragment (without leading $filter=) */
  clause: string;
  field: "RawInboundDiscriminator" | "RawOutboundDiscriminator" | "RawStatus";
}

const DISPLAY_TO_RAW = buildDisplayToRawDiscriminatorMap();

/**
 * Outbound workflow actions that filter on `RawStatus` (not outbound discriminator).
 * Matches how the SQL-only API models “current workflow step” for serviceable side.
 */
const RAW_STATUS_OUTBOUND_ACTIONS = new Set([
  "ShipmentAction",
  "CoveredAction",
  "InStockAction",
  "ExchangeRequestAction",
  "AlternativeAction",
  "CancellationAction",
]);

/**
 * Inverse of `mapRawOutboundDiscriminator` for **Shipped** — list rows can show
 * Shipped when `RawOutboundDiscriminator` is `ShipmentAction` even if `RawStatus`
 * is an inbound workflow value (e.g. `OffcoreShippingAction` while unserviceable
 * part is in OffCore Shipping).
 */
const buildShippedOutboundODataClause = (): string => {
  const rawStatusShippedClause = ["ShipmentAction", "ActionDetail"]
    .map((raw) => `RawStatus eq '${raw}'`)
    .join(" or ");
  return `(RawOutboundDiscriminator eq 'ShipmentAction' or (RawOutboundDiscriminator eq null and (${rawStatusShippedClause})))`;
};

/** Known toolbar labels → OData clause (must stay in sync with `sqlOnlyOrderMapper`). */
const UI_STATUS_TO_ODATA: Record<string, SqlOnlyStatusODataFilter> = {
  "Awaiting OffCore Info": {
    clause: "RawInboundDiscriminator eq null",
    field: "RawInboundDiscriminator",
  },
  "OffCore Part Info": {
    clause: "RawInboundDiscriminator eq 'OffcorePartAction'",
    field: "RawInboundDiscriminator",
  },
  "OffCore Shipping": {
    clause: "RawInboundDiscriminator eq 'OffcoreShippingAction'",
    field: "RawInboundDiscriminator",
  },
  [OFF_CORE_DETAIL_LABEL]: {
    clause: "RawInboundDiscriminator eq 'OffcoreDetailAction'",
    field: "RawInboundDiscriminator",
  },
  Cancelled: {
    clause: "RawStatus eq 'CancellationAction'",
    field: "RawStatus",
  },
  Shipped: {
    clause: buildShippedOutboundODataClause(),
    field: "RawStatus",
  },
  [NEUTRAL_COVERAGE_LABEL]: {
    clause: "RawStatus eq 'CoveredAction'",
    field: "RawStatus",
  },
  "Accept/Decline": {
    clause: "RawOutboundDiscriminator eq 'AcceptDeclineAction'",
    field: "RawOutboundDiscriminator",
  },
  [NEUTRAL_STOCK_LABEL]: {
    clause: "RawStatus eq 'InStockAction'",
    field: "RawStatus",
  },
  "Request Received": {
    clause: "RawStatus eq 'ExchangeRequestAction'",
    field: "RawStatus",
  },
  "Alternate Offered": {
    clause: "RawStatus eq 'AlternativeAction'",
    field: "RawStatus",
  },
};

/**
 * Fallback when the status dropdown returns a label not in `UI_STATUS_TO_ODATA`.
 */
const resolveDynamicSqlOnlyStatusFilter = (
  status: string,
): SqlOnlyStatusODataFilter | undefined => {
  const normalized = status.trim();
  if (!normalized) {
    return undefined;
  }

  const type = getStatusType(normalized);

  if (normalized.endsWith("Action") && !DISPLAY_TO_RAW[normalized]) {
    const field =
      type === "inbound"
        ? "RawInboundDiscriminator"
        : "RawOutboundDiscriminator";
    return {
      clause: `${field} eq '${normalized}'`,
      field,
    };
  }

  const raw = DISPLAY_TO_RAW[normalized];
  if (!raw) {
    return undefined;
  }

  if (type === "inbound") {
    if (normalized === "Awaiting OffCore Info") {
      return {
        clause: "RawInboundDiscriminator eq null",
        field: "RawInboundDiscriminator",
      };
    }
    return {
      clause: `RawInboundDiscriminator eq '${raw}'`,
      field: "RawInboundDiscriminator",
    };
  }

  if (RAW_STATUS_OUTBOUND_ACTIONS.has(raw)) {
    return {
      clause: `RawStatus eq '${raw}'`,
      field: "RawStatus",
    };
  }

  return {
    clause: `RawOutboundDiscriminator eq '${raw}'`,
    field: "RawOutboundDiscriminator",
  };
};

/** Resolve a toolbar/URL status label to a PBHOrdersSQLOnly `$filter` fragment. */
export const getSqlOnlyStatusODataFilter = (
  status: string | null | undefined,
): SqlOnlyStatusODataFilter | undefined => {
  if (!status) {
    return undefined;
  }
  const normalizedStatus =
    status === "Off Core Detail" ? OFF_CORE_DETAIL_LABEL : status;
  return (
    UI_STATUS_TO_ODATA[normalizedStatus] ??
    resolveDynamicSqlOnlyStatusFilter(normalizedStatus)
  );
};
